import { RequireConnectionClickable } from '@ui/features/Wallet/components/RequireConnectionClickable';
import {
  useApproveErc20Token,
  useIsNeedTokenApproval,
} from '@ui/hooks/useErc20Read';
import { Button } from '@ui/lib/park/ui/button';
import { PairResponsePairTypeEnum } from '@ui/services/backend/generated';
import { OrderType } from '@ui/types/trade';
import { useTranslation } from 'next-i18next';
import { useAccount } from 'wagmi';

import { useFromInput } from '../../../hooks/useFromInput';
import { useTradingPair } from '../../../store/useTradingPair';
import { TradeModuleConfirm } from '../../TradeModuleConfirm/TradeModuleConfirm';
import { useIsConfirmBtnDisabled } from '../hooks/useIsConfirmBtnDisabled';
import { useCollateralToken } from '../store/useCollateralToken';
import { useIsLong } from '../store/useIsLong';
import { useOrderType } from '../store/useOrderType';
import { btnStyle } from '../styles';

export const ConfirmButton = () => {
  const { t } = useTranslation();
  const tradingPair = useTradingPair((s) => s.useTradingPair());
  const isLong = useIsLong((s) => s._persistValue);
  const inputObject = useFromInput((s) => s.input);
  const inputAmount = inputObject.value.token;
  const displayTokenName =
    tradingPair.type === PairResponsePairTypeEnum.Forex
      ? tradingPair.baseName
      : tradingPair.baseSymbol;

  const payToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const orderType = useOrderType((s) => s._persistValue);
  const isMarket = orderType === OrderType.MARKET;

  const [isConfirmBtnDisabled, reason] = useIsConfirmBtnDisabled();

  const { isConnected } = useAccount();

  const isNeedTokenApproval = useIsNeedTokenApproval({
    tokenAddress: payToken?.address,
    requiredAmount: inputAmount,
  });

  const { writeContract: approvePayToken, isPending: isApprovalPending } =
    useApproveErc20Token({
      tokenAddress: payToken?.address,
      onSuccess: () => {
        isNeedTokenApproval.refetch();
      },
    });

  if (!isConnected) {
    return (
      <RequireConnectionClickable>
        <Button
          css={btnStyle}
          variant="solid"
          colorPalette={isLong ? 'priceUp' : 'priceDown'}
          loading={isApprovalPending}
          onClick={approvePayToken}
          data-cy="ConnectWalletButton"
        >
          {t('common.Connect Wallet')}
        </Button>
      </RequireConnectionClickable>
    );
  }

  if (
    !isConfirmBtnDisabled &&
    isNeedTokenApproval.data &&
    !payToken?.isNative
  ) {
    return (
      <Button
        css={btnStyle}
        variant="solid"
        colorPalette={isLong ? 'priceUp' : 'priceDown'}
        loading={isApprovalPending}
        onClick={approvePayToken}
        data-cy="ApproveButton"
      >
        {t('common.Approve {token}', {
          token: payToken?.displaySymbol,
        })}
      </Button>
    );
  }

  return (
    <TradeModuleConfirm>
      <RequireConnectionClickable>
        <Button
          css={btnStyle}
          variant="solid"
          colorPalette={isLong ? 'priceUp' : 'priceDown'}
          {...(!isConfirmBtnDisabled
            ? {
                color: 'var(--colors-color-palette-fg)',
                bg: 'var(--colors-color-palette-default)',
                _hover: { bg: 'var(--colors-color-palette-emphasized)' },
              }
            : {})}
          disabled={!!isConfirmBtnDisabled}
          data-cy="ConfirmButton"
          // bgColor={isLong ? 'var(--colors-price-price-up)' : 'var(--colors-price-price-down)'}
        >
          {reason
            ? reason
            : isMarket
            ? isLong
              ? t('common.Long {token}', {
                  token: displayTokenName,
                  interpolation: {
                    escapeValue: false,
                  },
                })
              : t('common.Short {token}', {
                  token: displayTokenName,
                  interpolation: {
                    escapeValue: false,
                  },
                })
            : t('common.Create Order')}
        </Button>
      </RequireConnectionClickable>
    </TradeModuleConfirm>
  );
};
